import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Product, ChangeProduct } from "../../models/AdminProductsModels";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { SubmitHandler, useForm } from "react-hook-form";
import { changeProduct, createProduct } from "../../adminProductsSlice";
import UploadFile from "../../../shared/components/UploadFile";
import { integerRegex, threeDecimalsRegex } from "../../../shared/validators";
import { FileModel } from "../../../shared/models/models";

export interface ProductDialogProps {
  open: boolean;
  onClose: () => void;
  product?: Product | null;
}

const ProductDialog = ({ open, onClose, product }: ProductDialogProps) => {
  const { manufacturers, categories } = useAppSelector(state => state.adminProducts);
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ChangeProduct>();

  const isNew: boolean = !product;
  const [file, setFile] = useState<FileModel | null>(product ? product.file : null);

  const handlerCreateFile = (file: FileModel | null) => {
    setFile(file);
  };

  const onSubmit: SubmitHandler<ChangeProduct> = (data: ChangeProduct) => {
    data.alternativeNumber = data.alternativeNumber || undefined;
    data.height = data.height ? Number(data.height) : undefined;
    data.width = data.width ? Number(data.width) : undefined;
    data.depth = data.depth ? Number(data.depth) : undefined;
    data.weight = data.weight ? Number(data.weight) : undefined;
    data.file = file;

    if (isNew) {
      handlerCreateProduct(data);
    } else {
      handlerChangeProduct(data);
    }
  };

  const handlerChangeProduct = async (model: ChangeProduct) => {
    if (product) {
      await dispatch(changeProduct({ id: product.id, model }));
      onClose();
    }
  };

  const handlerCreateProduct = async (model: ChangeProduct) => {
    await dispatch(createProduct(model));
    onClose();
  };

  useEffect(() => {
    if (!product) {
      return;
    }

    const changeProduct: ChangeProduct = {
      manufacturerId: product.manufacturer?.id,
      number: product.number,
      alternativeNumber: product.alternativeNumber,
      categoryId: product.category?.id,
      description: product.description,
      height: product.height,
      width: product.width,
      depth: product.depth,
      weight: product.weight,
      file: product.file
    };

    if (!changeProduct.manufacturerId || !changeProduct.categoryId) {
      console.error('Missing required fields for product');
      return;
    }

    reset(changeProduct);
  }, []);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px",
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            },

            ".MuiPaper-root": {
              width: "600px",
              display: "flex",
              justifyContent: "center",
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <p className="heading-2">{isNew ? "Create product" : "Edit product details"}</p>
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "#333333",
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <form id="form" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <InputLabel required>Manufacturer</InputLabel>
              <FormControl className="w-full">
                <Select
                  key="manufacturerId"
                  defaultValue={product?.manufacturer?.id || ""}
                  onClose={() => setTimeout(() => trigger("manufacturerId"))}
                  {...register("manufacturerId", { required: "Field is required" })}
                  error={!!errors.manufacturerId}
                >
                  {manufacturers?.map((manufacturer) => (
                    <MenuItem key={manufacturer.id} value={manufacturer.id}>
                      {manufacturer.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>{errors.manufacturerId?.message}</FormHelperText>
              </FormControl>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <InputLabel required>Product number</InputLabel>
                <TextField
                  className="w-full"
                  {...register("number", { required: true })}
                  error={!!errors.number}
                  helperText={errors.number ? "Field is required" : ""}
                />
              </div>

              <div>
                <InputLabel>Alternative number</InputLabel>
                <TextField
                  className="w-full"
                  {...register("alternativeNumber")}
                  error={!!errors.alternativeNumber}
                  helperText={errors.alternativeNumber?.message}
                />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel required>Category</InputLabel>
              <FormControl className="w-full">
                <Select
                  defaultValue={product?.category?.id || ""}
                  onClose={() => setTimeout(() => trigger("categoryId"))}
                  {...register("categoryId", { required: "Field is required" })}
                  error={!!errors.categoryId}
                >
                  {categories?.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>{errors.categoryId?.message}</FormHelperText>
              </FormControl>
            </div>

            <div className="mt-4">
              <InputLabel>Description</InputLabel>
              <TextField
                className="w-full textarea"
                multiline
                rows={3}
                {...register("description")}
              />
            </div>

            <div className="mt-4 flex gap-4">
              <div>
                <InputLabel>Height, mm</InputLabel>
                <TextField
                  className="w-full"
                  {...register("height", { pattern: { value: integerRegex, message: "Invalid value" } })}
                  error={!!errors.height}
                  helperText={errors.height?.message}
                />
              </div>

              <div>
                <InputLabel>Width, mm</InputLabel>
                <TextField
                  className="w-full"
                  {...register("width", { pattern: { value: integerRegex, message: "Invalid value" } })}
                  error={!!errors.width}
                  helperText={errors.width?.message}
                />
              </div>

              <div>
                <InputLabel>Depth, mm</InputLabel>
                <TextField
                  className="w-full"
                  {...register("depth", { pattern: { value: integerRegex, message: "Invalid value" } })}
                  error={!!errors.depth}
                  helperText={errors.depth?.message}
                />
              </div>

              <div>
                <InputLabel>Weight, kg</InputLabel>
                <TextField
                  className="w-full"
                  {...register("weight", { pattern: { value: threeDecimalsRegex, message: "Invalid value" } })}
                  error={!!errors.weight}
                  helperText={errors.weight?.message}
                />
              </div>
            </div>

            <div className="mt-4">
              <InputLabel>Image</InputLabel>
              <UploadFile file={file} variant="image" onChangeFile={handlerCreateFile} />
            </div>
          </form>
        </DialogContent>

        <DialogActions className="flex">
          <Button variant="text" className="w-full" onClick={onClose}>
            Cancel
          </Button>

          <Button form="form" type="submit" variant="contained" className="w-full">
            {isNew ? "Create" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
export default ProductDialog;
